import { useState, useEffect } from "react";
import { useLocation, Link, useNavigate } from "react-router-dom";
import PropTypes from "prop-types";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Icon from "@mui/material/Icon";
import Badge from "@mui/material/Badge";
import InputBase from "@mui/material/InputBase";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

import GlobalSearch from "Pages/controlPlane/components/GlobalSearch";

import {
  navbar,
  navbarContainer,
  navbarRow,
} from "examples/Navbars/DashboardNavbar/styles";

import {
  useMaterialUIController,
  setMiniSidenav,
} from "context";

function DashboardNavbar({ absolute, light, isMini }) {
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav } = controller;
  const [search, setSearch] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);
  const navigate = useNavigate();

  // Cmd+K / Ctrl+K to open global search
  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setSearchOpen(true);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, []);

  const handleMiniSidenav = () => setMiniSidenav(dispatch, !miniSidenav);

  const handleSearchSelect = (tenant) => {
    navigate(`/tenants/${tenant.id}`);
  };

  return (
    <AppBar
      position="sticky"
      color="inherit"
      sx={(theme) => navbar(theme, {})}
    >
      <Toolbar sx={(theme) => navbarContainer(theme)}>
        {/* Left: Search */}
        <MDBox sx={(theme) => navbarRow(theme, { isMini })} flex={1}>
          <MDBox
            sx={{
              position: "relative",
              maxWidth: 400,
              width: "100%",
            }}
          >
            <Icon
              sx={{
                position: "absolute",
                left: 10,
                top: "50%",
                transform: "translateY(-50%)",
                color: "#BDC3C7",
                fontSize: "18px !important",
              }}
            >
              search
            </Icon>
            <InputBase
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onClick={() => setSearchOpen(true)}
              placeholder="Search tenants… (Cmd+K)"
              sx={{
                width: "100%",
                pl: 4.5,
                pr: 1.5,
                py: 0.75,
                fontSize: 13,
                border: "1px solid #E8EDF2",
                borderRadius: "8px",
                backgroundColor: "#F8FAFC",
                cursor: "pointer",
                "&:hover": { borderColor: "#D5DBDB" },
                "&.Mui-focused": { borderColor: "#2E86AB", backgroundColor: "#fff" },
              }}
              readOnly
            />
          </MDBox>
        </MDBox>

        {isMini ? null : (
          <MDBox display="flex" alignItems="center" gap={2}>
            {/* Alert banner */}
            <MDBox
              sx={{
                display: { xs: "none", md: "flex" },
                alignItems: "center",
                gap: 1,
                backgroundColor: "#FDEBD0",
                px: 1.5,
                py: 0.5,
                borderRadius: "8px",
                fontSize: 12,
              }}
            >
              <Icon sx={{ color: "#E67E22", fontSize: "14px !important" }}>warning</Icon>
              <MDTypography variant="caption" sx={{ color: "#784212" }}>
                <strong>Initech</strong>: Payment failed — 3rd retry
              </MDTypography>
            </MDBox>

            {/* Notification bell */}
            <IconButton size="medium" sx={{ color: "#5D6D7E" }}>
              <Badge badgeContent={3} color="error" sx={{ "& .MuiBadge-badge": { fontSize: 10, height: 16, minWidth: 16 } }}>
                <Icon sx={{ fontSize: "24px !important" }}>notifications_none</Icon>
              </Badge>
            </IconButton>

            {/* Settings */}
            <IconButton size="medium" sx={{ color: "#5D6D7E" }}>
              <Icon sx={{ fontSize: "24px !important" }}>settings_outlined</Icon>
            </IconButton>

            {/* Profile */}
            <Link to="/authentication/sign-in">
              <IconButton size="medium" sx={{ color: "#5D6D7E" }}>
                <Icon sx={{ fontSize: "24px !important" }}>person_outline</Icon>
              </IconButton>
            </Link>

            {/* Mobile menu toggle */}
            <IconButton
              size="small"
              sx={{ color: "#5D6D7E", display: { xl: "none" } }}
              onClick={handleMiniSidenav}
            >
              <Icon>{miniSidenav ? "menu_open" : "menu"}</Icon>
            </IconButton>
          </MDBox>
        )}
      </Toolbar>

      {/* Global Search Overlay */}
      <GlobalSearch
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onSelect={handleSearchSelect}
      />
    </AppBar>
  );
}

DashboardNavbar.defaultProps = {
  absolute: false,
  light: false,
  isMini: false,
};

DashboardNavbar.propTypes = {
  absolute: PropTypes.bool,
  light: PropTypes.bool,
  isMini: PropTypes.bool,
};

export default DashboardNavbar;
